/**
 * Centralized color configuration for the Tech Radar application
 *
 * This file provides a single source of truth for all colors used in the application,
 * preventing duplication and making theme changes easier to manage.
 */

import type { RingId } from '@/types/radar';

/**
 * Ring color configuration
 * Each ring has a primary color, light variant for gradients, and fill color with opacity
 * These match the ThoughtWorks-style radar design
 */
export const RING_COLORS = {
  adopt: {
    primary: '#10b981', // Emerald green
    light: '#34d399',
    fill: 'rgba(16, 185, 129, 0.05)',
  },
  trial: {
    primary: '#3b82f6', // Blue
    light: '#60a5fa',
    fill: 'rgba(59, 130, 246, 0.05)',
  },
  assess: {
    primary: '#8b5cf6', // Purple
    light: '#a78bfa',
    fill: 'rgba(139, 92, 246, 0.05)',
  },
  hold: {
    primary: '#64748b', // Slate
    light: '#818cf8',
    fill: 'rgba(100, 116, 139, 0.05)',
  },
} as const;

/**
 * UI element colors used throughout the application
 */
export const UI_COLORS = {
  divider: '#e0e0e0',
  quadrantLabel: '#1e293b',
  quadrantLabelHover: '#0f172a',
  moveInward: '#10b981',
  moveOutward: '#ef4444',
  newBadge: '#ffffff',
  blipStroke: '#ffffff',
  blipHover: 'rgba(0, 0, 0, 0.1)',
} as const;

/**
 * Helper function to get ring color by ring ID
 */
export const getRingColor = (ringId: RingId): string => {
  return RING_COLORS[ringId]?.primary ?? '#000000';
};

/**
 * Helper function to get ring light color by ring ID (for gradients)
 */
export const getRingLightColor = (ringId: RingId): string => {
  return RING_COLORS[ringId]?.light ?? '#000000';
};

/**
 * Helper function to get ring fill color by ring ID
 */
export const getRingFillColor = (ringId: RingId): string => {
  return RING_COLORS[ringId]?.fill ?? 'rgba(0, 0, 0, 0.05)';
};

/**
 * Generate gradient definition for a ring
 */
export const getRingGradient = (ringId: RingId, index: number) => ({
  id: `ringGradient${index + 1}`,
  color1: getRingColor(ringId),
  color2: getRingLightColor(ringId),
});
